import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Task } from '@/api';
import { useWebsocket } from '@/common/providers/websocket-provider';
import { addOrUpdateAllTasksCache } from './tasks-cache';

/**
 * Keeps `useFetchTask` fresh: subscribes to the company's tasks and writes a
 * pushed update for this task straight into the cache.
 */
export function useEnableTaskSockets(taskId: string, companyId: string) {
    const queryClient = useQueryClient();

    const { subscribe, unsubscribe, on, off } = useWebsocket();
    useEffect(() => {
        const ids = subscribe([{ resource: `companies#${companyId}.tasks` }]);
        return () => {
            unsubscribe(ids);
        };
    }, [companyId, subscribe, unsubscribe]);

    useEffect(() => {
        const tasksCallback = (message: unknown) => {
            const {
                data: { task }
            } = message as {
                data: { task: Task };
            };

            if (task.id === taskId) {
                // All, because if we're on the tasks page and click back, we expect the tasks list to contain the updated task instantly
                addOrUpdateAllTasksCache(queryClient, task);
            }
        };

        on('tasks_v3', tasksCallback);

        return () => {
            off('tasks_v3', tasksCallback);
        };
    }, [on, off, queryClient, taskId]);
}
